import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { BubbleIcon } from '@/components/Icons/Bubble';
import { Info } from '@/components/Icons/Info';
import { TextReply } from '@/components/Icons/TextReply';
import { OpeningMessage } from '@/components/OpeningMessage/OpeningMessage';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useController } from '@/hooks/useController';
import { useOldElementSize } from '@/hooks/useOldElementSize';
import { useWidgetState } from '@/hooks/useWidgetState';
import { widgetPositionSelector, widgetAlignmentSelector, launcherSelector, themeSelector, bubbleImageSelector, webchatAriaLabelSelector, } from '@/state/config';
import { hasUnreadMessagesSelector, isWidgetOpenState } from '@/state/session';
import { CustomIcon } from '../Icons/CustomIcon';
import styles from './Launcher.module.scss';
export const Launcher = () => {
    const [launcherRef, { width, height }] = useOldElementSize();
    const { t } = useTranslation();
    const { notifyWidgetResize } = useController();
    const { openWidget } = useWidgetState();
    const { trackEvent } = useAnalytics();
    const theme = useRecoilValue(themeSelector);
    const isOpen = useRecoilValue(isWidgetOpenState);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    const widgetAlignment = useRecoilValue(widgetAlignmentSelector);
    const { size, type, show } = useRecoilValue(launcherSelector);
    const hasUnreadMessages = useRecoilValue(hasUnreadMessagesSelector);
    const bubbleImage = useRecoilValue(bubbleImageSelector);
    const ariaLabel = useRecoilValue(webchatAriaLabelSelector) || t('buttons.launcher_label');
    useEffect(() => {
        if (width && height && !isOpen) {
            notifyWidgetResize({
                width: `${width + 30}px`,
                height: `${height + 40}px`,
            });
        }
    }, [height, isOpen, notifyWidgetResize, width]);
    const handleClick = useCallback(() => {
        // Track launcher click event
        trackEvent(AnalyticsEvent.LauncherClicked);
        openWidget();
    }, [openWidget, trackEvent]);
    const renderLauncher = useCallback(() => {
        if (type === 'help') {
            return (_jsxs("button", { className: cn("launcher-btn" /* TestClassName.LauncherButton */, styles[type], {
                    [styles.hidden]: isOpen,
                }), tabIndex: 0, onClick: handleClick, "aria-label": ariaLabel, children: [_jsx(Info, {}), " ", _jsx("p", { className: styles.text, children: t('launchers.help') })] }));
        }
        if (type === 'feedback') {
            return (_jsxs("button", { className: cn("launcher-btn" /* TestClassName.LauncherButton */, styles[type], {
                    [styles.hidden]: isOpen,
                }), tabIndex: 0, onClick: handleClick, "aria-label": ariaLabel, children: [_jsx(TextReply, {}), " ", _jsx("p", { className: styles.text, children: t('launchers.feedback') })] }));
        }
        if (bubbleImage) {
            return (_jsx("div", { className: cn(styles[type], { [styles.hidden]: isOpen }), children: _jsx(CustomIcon, { onClick: handleClick, theme: theme, size: size, imgUrl: bubbleImage, ariaLabel: ariaLabel }) }));
        }
        return (_jsx("div", { className: cn(styles[type], { [styles.hidden]: isOpen }), children: _jsx(BubbleIcon, { className: "launcher-btn" /* TestClassName.LauncherButton */, onClick: handleClick, theme: theme, size: size, "aria-label": ariaLabel }) }));
    }, [ariaLabel, bubbleImage, handleClick, isOpen, size, t, theme, type]);
    // If the user clicks on the launcher before socket is connected,
    // the state will be back to Idle not loading the conversation.
    return (_jsxs("div", { ref: launcherRef, className: cn(styles.launcher, styles[theme], styles[widgetPosition], {
            [styles.isPositionLeft]: widgetAlignment === 'left',
        }), children: [_jsx(OpeningMessage, {}), _jsxs("div", { className: cn(styles[`type--${type}`], styles.bottom, {
                    [styles.open]: open,
                    [styles.animate]: hasUnreadMessages,
                    [styles.hidden]: !show,
                    [styles.isPositionLeft]: widgetAlignment === 'left',
                }), children: [hasUnreadMessages && _jsx("span", { className: styles.dot }), renderLauncher()] })] }));
};
